import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { ACTION_TYPES, useContractSearchDispatch } from './contract-search-context';
import { useBaseData } from '../../../../application/context/base-data-context';
import { useFeedback } from '../../../../hooks/use-feedback';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { IContractPageRequest } from '../../../../model/scp/contract/request/contract-paged-request';
import { parseNumberInput } from '../../../../utils/format';
import { parseSort } from '../../shared/scp-utils';
import { ContractSortField } from '../../../../model/scp/contract/contract-sort-field';
import { SortDirection } from '../../../../model/scp/base/sort-direction';
import { IContractPagedResponse } from '../../../../model/scp/contract/response/contract-paged-response';

const DEFAULT_SORT = { sortBy: ContractSortField.UpdatedDate, sortDirection: SortDirection.Descending };

type SearchParameters = IContractPageRequest & { page: number; pageSize: number };

const EMPTY_FILTERS: Partial<SearchParameters> = { ...DEFAULT_SORT, term: undefined, clientId: undefined, categoryProcessId: undefined, contractTypeId: undefined, year: undefined };

export const useContractSearchController = () => {
    const navigate = useNavigate();
    const { showError, showSuccess, confirmDeleteWithJustification, withLoading } = useFeedback();
    const dispatch = useContractSearchDispatch();
    const { updateParameters, handleSearch, handleClearFilters, handlePaginationChange } = useSearchActions<SearchParameters>(dispatch, ACTION_TYPES, EMPTY_FILTERS);
    const baseData = useBaseData();

    // Usa as tabelas já carregadas no início da sessão; se ainda não houver, busca direto na API
    useEffect(() => {
        if (baseData.loaded) {
            setLookups(baseData.typeContract, baseData.categoryProcess);
            return;
        }
        Promise.all([BaseAppService.listTypeContract(), BaseAppService.listCategoryProcess()])
            .then(([typeContract, categoryProcess]) => setLookups(typeContract, categoryProcess))
            .catch((error: any) => showError('Não foi possível carregar os filtros de pesquisa', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseData.loaded]);

    function setLookups(typeContract: unknown, categoryProcess: unknown) {
        dispatch({ type: ACTION_TYPES.setTypeContract, payload: typeContract });
        dispatch({ type: ACTION_TYPES.setCategoryProcess, payload: categoryProcess });
    }

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        switch (name) {
            case 'input-term':
                return updateParameters({ term: value || undefined });
            case 'select-category-process':
                return updateParameters({ categoryProcessId: parseNumberInput(value) });
            case 'select-contract-type':
                return updateParameters({ contractTypeId: parseNumberInput(value) });
            case 'select-client':
                return updateParameters({ clientId: parseNumberInput(value) });
            case 'input-year':
                return updateParameters({ year: parseNumberInput(value) });
            case 'select-sort':
                return updateParameters(parseSort(value) ?? DEFAULT_SORT);
        }
    }

    function navigateToCreate() {
        navigate('/contratos/publicar');
    }

    function navigateToEdit(row: IContractPagedResponse) {
        navigate(`/contratos/publicar/${row.id}`);
    }

    function navigateToAmendments(row: IContractPagedResponse) {
        navigate(`/contratos/${row.id}/aditivos`);
    }

    function handleDelete(row: IContractPagedResponse, onDeleted: () => void) {
        confirmDeleteWithJustification(
            `Tem certeza que deseja excluir o contrato "${row.contractEffortNumber}"?`,
            'Informe a justificativa para excluir o contrato',
            async (justification) => {
                try {
                    await withLoading(() => ContractAppService.deleteContract(justification, row.id));
                    showSuccess('Contrato excluído com sucesso!');
                    onDeleted();
                } catch (error: any) {
                    showError('Não foi possível excluir o contrato', error);
                }
            }
        );
    }

    return {
        handleOnChange,
        handleSearch,
        handleClearFilters,
        handlePaginationChange,
        navigateToCreate,
        navigateToEdit,
        navigateToAmendments,
        handleDelete,
    };
};
